/**
 * API-Funktionen für Produkt-Bild Integration
 */
import { apiClient } from '@/lib/api-client'
import type {
  TransferResponse,
  LinkRequest,
  LinkResponse,
  AIGeneratedImagesResponse,
  ProductMetadata,
  GeneratedImageWithMetadata,
} from '@/types/product-images'

// =============================================================================
// TRANSFER
// =============================================================================

/**
 * Überführt ein generiertes Bild in ein Produkt-Asset.
 */
export async function transferImageToProduct(
  productId: string,
  generatedImageId: string,
  options?: { presetType?: string; assetType?: string }
): Promise<TransferResponse> {
  const response = await apiClient.post<TransferResponse>(
    `/api/v1/products/${productId}/images/transfer`,
    {
      generated_image_id: generatedImageId,
      preset_type: options?.presetType,
      asset_type: options?.assetType || 'AI_GENERATED',
    }
  )
  return response.data
}

/**
 * Überführt ein Bild von der Galerie zu einem Produkt.
 *
 * @param targetType - 'collection' für Bildersammlung (product_asset_links), 'ai_reference' für KI-Referenzbilder
 * @param slot - Slot-Nummer (1-4 für ai_reference, 1-10 für collection)
 * @param channelId - Sales Channel ID (UUID) für Channel-Slot bei target_type='collection'
 * @param channelSlot - Slot im Channel (1-10), wenn nicht gesetzt nimmt das Backend den nächsten freien
 */
export async function transferFromGalleryToProduct(
  imageId: string,
  productId: string,
  options?: {
    presetType?: string
    assetType?: string
    targetType?: 'ai_reference' | 'collection'
    slot?: number
    channelId?: string
    channelSlot?: number
  }
): Promise<TransferResponse> {
  const response = await apiClient.post<TransferResponse>(
    `/api/v1/images/${imageId}/transfer-to-product/${productId}`,
    {
      preset_type: options?.presetType,
      asset_type: options?.assetType || 'AI_GENERATED',
      target_type: options?.targetType || 'collection',
      slot: options?.slot || 1,
      channel_id: options?.channelId,
      channel_slot: options?.channelSlot,
    }
  )
  return response.data
}

// =============================================================================
// LINK / UNLINK
// =============================================================================

/**
 * Verknüpft ein Asset mit einem Produkt.
 */
export async function linkAssetToProduct(
  productId: string,
  assetId: string,
  options?: LinkRequest
): Promise<LinkResponse> {
  const response = await apiClient.post<LinkResponse>(
    `/api/v1/products/${productId}/images/${assetId}/link`,
    options
  )
  return response.data
}

/**
 * Entfernt die Verknüpfung zwischen Asset und Produkt.
 */
export async function unlinkAssetFromProduct(
  productId: string,
  assetId: string,
  assetType?: string
): Promise<{ success: boolean }> {
  const params = assetType ? `?asset_type=${assetType}` : ''
  const response = await apiClient.delete<{ success: boolean }>(
    `/api/v1/products/${productId}/images/${assetId}/unlink${params}`
  )
  return response.data
}

/**
 * Verknüpft ein Bild mit einem Produkt (ohne Transfer zu Asset).
 */
export async function linkImageToProduct(
  imageId: string,
  productId: string
): Promise<{ success: boolean }> {
  const response = await apiClient.post<{ success: boolean }>(
    `/api/v1/images/${imageId}/link-to-product/${productId}`
  )
  return response.data
}

// =============================================================================
// AI GENERATED IMAGES
// =============================================================================

/**
 * Lädt alle KI-generierten Assets eines Produkts.
 */
export async function getProductAIGeneratedImages(
  productId: string,
  options?: {
    approvalStatus?: 'pending' | 'approved' | 'rejected'
    includePendingImages?: boolean
  }
): Promise<AIGeneratedImagesResponse> {
  const params = new URLSearchParams()
  if (options?.approvalStatus) {
    params.append('approval_status', options.approvalStatus)
  }
  if (options?.includePendingImages !== undefined) {
    params.append('include_pending_images', String(options.includePendingImages))
  }
  
  const queryString = params.toString() ? `?${params.toString()}` : ''
  const response = await apiClient.get<AIGeneratedImagesResponse>(
    `/api/v1/products/${productId}/images/ai-generated${queryString}`
  )
  return response.data
}

/**
 * Lädt generierte Bilder eines Produkts.
 */
export async function getImagesByProduct(
  productId: string,
  options?: {
    includeTransferred?: boolean
    includeMetadata?: boolean
    limit?: number
  }
): Promise<{
  success: boolean
  images: GeneratedImageWithMetadata[]
  metadata?: ProductMetadata
  stats?: Record<string, number>
}> {
  const params = new URLSearchParams()
  if (options?.includeTransferred !== undefined) {
    params.append('include_transferred', String(options.includeTransferred))
  }
  if (options?.includeMetadata !== undefined) {
    params.append('include_metadata', String(options.includeMetadata))
  }
  if (options?.limit) {
    params.append('limit', String(options.limit))
  }
  
  const queryString = params.toString() ? `?${params.toString()}` : ''
  const response = await apiClient.get<{
    success: boolean
    images: GeneratedImageWithMetadata[]
    metadata?: ProductMetadata
    stats?: Record<string, number>
  }>(`/api/v1/images/by-product/${productId}${queryString}`)
  return response.data
}

/**
 * Lädt alle Bilder die noch nicht überführt wurden.
 */
export async function getPendingTransferImages(
  limit?: number
): Promise<{
  success: boolean
  images: GeneratedImageWithMetadata[]
}> {
  const params = limit ? `?limit=${limit}` : ''
  const response = await apiClient.get<{
    success: boolean
    images: GeneratedImageWithMetadata[]
  }>(`/api/v1/images/pending-transfer${params}`)
  return response.data
}

// =============================================================================
// METADATA
// =============================================================================

/**
 * Lädt Produkt-Metadaten.
 */
export async function getProductMetadata(
  productId: string
): Promise<{
  success: boolean
  metadata: ProductMetadata
}> {
  const response = await apiClient.get<{
    success: boolean
    metadata: ProductMetadata
  }>(`/api/v1/products/${productId}/images/metadata`)
  return response.data
}

// =============================================================================
// APPROVAL
// =============================================================================

/**
 * Genehmigt ein Asset für ein Produkt.
 */
export async function approveProductImage(
  productId: string,
  junctionId: string
): Promise<{ success: boolean }> {
  const response = await apiClient.post<{ success: boolean }>(
    `/api/v1/products/${productId}/images/${junctionId}/approve`
  )
  return response.data
}

/**
 * Lehnt ein Asset für ein Produkt ab.
 */
export async function rejectProductImage(
  productId: string,
  junctionId: string
): Promise<{ success: boolean }> {
  const response = await apiClient.post<{ success: boolean }>(
    `/api/v1/products/${productId}/images/${junctionId}/reject`
  )
  return response.data
}

/**
 * Aktualisiert den Freigabestatus eines Bildes.
 */
export async function updateImageApprovalStatus(
  imageId: string,
  approvalStatus: 'pending' | 'approved' | 'rejected'
): Promise<{ success: boolean }> {
  const response = await apiClient.put<{ success: boolean }>(
    `/api/v1/images/${imageId}/approval-status`,
    { approval_status: approvalStatus }
  )
  return response.data
}

/**
 * Markiert ein Bild als KI-generiert.
 */
export async function markImageAsAIGenerated(
  imageId: string,
  generationSource?: 'product' | 'manual' | 'template'
): Promise<{ success: boolean }> {
  const response = await apiClient.post<{ success: boolean }>(
    `/api/v1/images/${imageId}/mark-ai-generated`,
    { generation_source: generationSource || 'manual' }
  )
  return response.data
}

